import { useAuthUser, useTeamGet, useTeamSelect } from '@/hooks';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ArrowDown, ExternalLink, Search, X } from 'react-feather';
import { useDebounce } from 'use-debounce';

import { AutoComplete, Input, message } from 'antd/lib';
import { Button, TeamSelector } from '@/components';
import { Typography } from '@/components/typography';
import { Wrapper } from '@/components/wrapper';
import { openContentHubInNewTab } from '@/utils/contentHub';
import { useContentHubPaths } from '@/hooks/client/useContentHubPaths';
import { useContentHubSearch } from '@/hooks/client/useContentHubSearch';
import { useContentHubAutoCompletion } from '@/hooks/client/useContentHubAutoCompletion';
import { isProduction } from '@/utils/environment';

import { ContentHubTree } from './components/ContentHubTree';
import { ContentHubMediaGrid } from './components/ContentHubMediaGrid';
import styles from './ContentHub.module.css';

function normalizeHandle(h: string | null | undefined) {
  return (h ?? '').trim().replace(/^@/, '').toLowerCase();
}

function normalizeName(n: string | null | undefined) {
  return (n ?? '').trim().toLowerCase();
}

function ContentHubProduction() {
  const user = useAuthUser();
  const { t } = useTranslation();

  const heading = <Typography sizes={['h2']}>{t('navigationLink.contentHub')}</Typography>;

  const openHub = () => {
    if (!user.permissions?.show_menu_content_hub || !user.team?.content_hub_url) {
      message.error(t('navigationLink.contentHubUnavailable'));
      return;
    }
    openContentHubInNewTab(user.team.content_hub_url);
  };

  if (!user.permissions?.show_menu_content_hub || !user.team?.content_hub_url) {
    return (
      <Wrapper heading={heading} hideFooter>
        <div className={styles.emptyState}>
          <div>
            <svg
              width="50"
              height="50"
              viewBox="0 0 50 50"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M36.1109 12.2224C38.0265 12.2224 39.8643 12.9824 41.2176 14.338C42.5731 15.6913 43.3331 17.5291 43.3331 19.4446V42.7778C43.3331 44.6934 42.5731 46.5312 41.2176 47.8845C39.8643 49.24 38.0265 50 36.1109 50H7.22219C5.30664 50 3.46887 49.24 2.11555 47.8845C0.759996 46.5312 0 44.6934 0 42.7778V19.4446C0 17.5291 0.759996 15.6913 2.11555 14.338C3.46887 12.9824 5.30664 12.2224 7.22219 12.2224H36.1109ZM36.1109 15.5557H7.22219C6.19108 15.5557 5.2022 15.9646 4.47331 16.6957C3.7422 17.4246 3.33332 18.4135 3.33332 19.4446V42.7778C3.33332 43.8089 3.7422 44.7978 4.47331 45.5267C5.2022 46.2578 6.19108 46.6667 7.22219 46.6667H36.1109C37.142 46.6667 38.1309 46.2578 38.8598 45.5267C39.5909 44.7978 39.9998 43.8089 39.9998 42.7778V19.4446C39.9998 18.4135 39.5909 17.4246 38.8598 16.6957C38.1309 15.9646 37.142 15.5557 36.1109 15.5557Z"
                fill="black"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M5.86894 13.6002L7.2156 5.9691C7.54893 4.08244 8.61559 2.40467 10.1845 1.3069C11.7534 0.206906 13.6956 -0.221981 15.5822 0.109128L44.031 5.12688C45.9176 5.45799 47.5954 6.52688 48.6932 8.09576C49.7932 9.66464 50.2221 11.6068 49.891 13.4935L45.8376 36.4712C45.5065 38.3578 44.4376 40.0356 42.8688 41.1334C42.7643 41.2067 42.6576 41.2778 42.5488 41.3445C42.0354 41.6667 41.3888 41.6823 40.8577 41.3889C40.3288 41.0956 39.9999 40.5378 39.9999 39.9312V19.4446C39.9999 18.4135 39.591 17.4246 38.8599 16.6957C38.131 15.9646 37.1421 15.5557 36.111 15.5557H7.51115C7.01782 15.5557 6.55116 15.3379 6.23338 14.9602C5.91783 14.5824 5.7845 14.0846 5.86894 13.6002ZM43.3332 31.4823L46.6065 12.9135C46.7865 11.898 46.5554 10.8535 45.9643 10.0091C45.371 9.16242 44.4688 8.58909 43.4532 8.40909L15.0022 3.39356C13.9867 3.21356 12.9422 3.44467 12.0978 4.03578C11.2534 4.62911 10.6778 5.53132 10.4978 6.54688L9.49781 12.2224H36.111C38.0266 12.2224 39.8643 12.9824 41.2177 14.3379C42.5732 15.6913 43.3332 17.529 43.3332 19.4446V31.4823Z"
                fill="black"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M43.3331 39.4445V42.7778C43.3331 44.6934 42.5731 46.5311 41.2176 47.8845C39.8643 49.24 38.0265 50 36.1109 50H7.22219C5.30664 50 3.46887 49.24 2.11555 47.8845C0.759996 46.5311 0 44.6934 0 42.7778V39.4445C0 39.0334 0.153333 38.6356 0.428887 38.3289L7.04219 30.9801C8.21996 29.6734 9.89995 28.9357 11.6599 28.9579C13.4177 28.9779 15.0799 29.7557 16.2244 31.0912L17.9532 33.1045C18.0555 33.2268 18.2066 33.2979 18.3666 33.3001C18.5266 33.3023 18.6799 33.2334 18.7866 33.1156L23.8332 27.509C24.9821 26.2312 26.6176 25.4979 28.3354 25.4868C30.0554 25.4757 31.6998 26.189 32.8643 27.4534L42.8909 38.3134C43.1753 38.6223 43.3331 39.0245 43.3331 39.4445ZM39.9998 40.0956L30.4154 29.7134C29.8865 29.1401 29.1376 28.8157 28.3576 28.8201C27.5754 28.8246 26.8332 29.1579 26.311 29.7401L21.2643 35.3445C20.5155 36.1779 19.4466 36.6467 18.3266 36.6334C17.2088 36.6201 16.1488 36.1245 15.4221 35.2756L13.6933 33.2601C13.1733 32.6534 12.4177 32.3001 11.6177 32.2912C10.8199 32.2801 10.0555 32.6156 9.51995 33.209L3.33332 40.0845V42.7778C3.33332 43.8089 3.7422 44.7978 4.47331 45.5267C5.2022 46.2578 6.19108 46.6667 7.22219 46.6667H36.1109C37.142 46.6667 38.1309 46.2578 38.8598 45.5267C39.5909 44.7978 39.9998 43.8089 39.9998 42.7778V40.0956Z"
                fill="black"
              />
              <path
                d="M11.6666 25.0001C13.5075 25.0001 14.9999 23.5078 14.9999 21.6668C14.9999 19.8259 13.5075 18.3335 11.6666 18.3335C9.82563 18.3335 8.33325 19.8259 8.33325 21.6668C8.33325 23.5078 9.82563 25.0001 11.6666 25.0001Z"
                fill="black"
              />
            </svg>

            <h3>{t('hub.title')}</h3>
            <p>{t('hub.description')}</p>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper heading={heading} hideFooter>
      <div className={styles.emptyState}>
        <div>
          <Typography sizes={['body']}>{t('contentHub.landingHint')}</Typography>
          <Button theme="cta" onClick={openHub}>
            <ExternalLink size={16} aria-hidden />
            <span>{t('contentHub.openButton')}</span>
          </Button>
        </div>
      </div>
    </Wrapper>
  );
}

function ContentHubStaging() {
  const user = useAuthUser();
  const { t } = useTranslation();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectionNonce, setSelectionNonce] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebounce(searchText, 350);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [selectedInfluencerHandle, setSelectedInfluencerHandle] = useState<string | null>(null);
  const suppressNextAutoOpenRef = useRef(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMediaKeys, setSelectedMediaKeys] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [isAutoLoadingMore, setIsAutoLoadingMore] = useState(false);
  const [loadMoreDisabled, setLoadMoreDisabled] = useState(false);

  const { data: { teams: switchableTeams } = {}, isLoading: isLoadingTeams } = useTeamGet(
    user?.team?.team_id
  );

  const memoTeams = useMemo(() => (switchableTeams as Api.Team[]) || [], [switchableTeams]);

  const { isLoading: isSwitchingTeam, mutate: teamSwitch } = useTeamSelect({
    teamId: user?.team?.team_id
  });

  const { data: pathsData, isLoading: isLoadingPaths } = useContentHubPaths();
  const teams = pathsData?.tree ?? [];
  const pathRows = pathsData?.rows ?? [];
  const userTeamId = user?.team?.team_id ?? null;
  const isHubEmpty = !isLoadingPaths && teams.length === 0;
  const prevUserTeamIdRef = useRef<string | null>(null);

  const rowByCollaborationId = useMemo(() => {
    const map = new Map<string, Api.Client.ContentHubPathRow>();
    pathRows.forEach((r) => map.set(r.collaboration_id, r));
    return map;
  }, [pathRows]);

  const rowByBrandId = useMemo(() => {
    const map = new Map<string, Api.Client.ContentHubPathRow>();
    pathRows.forEach((r) => map.set(r.brand_id, r));
    return map;
  }, [pathRows]);

  const rowByCampaignId = useMemo(() => {
    const map = new Map<string, Api.Client.ContentHubPathRow>();
    pathRows.forEach((r) => map.set(r.campaign_id, r));
    return map;
  }, [pathRows]);

  const rowByTeamId = useMemo(() => {
    const map = new Map<string, Api.Client.ContentHubPathRow>();
    pathRows.forEach((r) => map.set(r.team_id, r));
    return map;
  }, [pathRows]);

  useEffect(() => {
    if (selectedFolderId) return;
    if (isSwitchingTeam) return;
    const firstTeamId = userTeamId ?? teams?.[0]?.id;
    if (firstTeamId) setSelectedFolderId(firstTeamId);
  }, [isSwitchingTeam, teams, selectedFolderId, userTeamId]);

  useEffect(() => {
    if (!userTeamId) return;
    if (prevUserTeamIdRef.current === userTeamId) return;
    prevUserTeamIdRef.current = userTeamId;

    // When current team changes (or first loads), always select the top-level Team node
    // and bump nonce so content refetches for the new team context.
    setSelectedFolderId(userTeamId);
    setSelectionNonce((n) => n + 1);
  }, [userTeamId]);

  const folderType = useMemo(() => {
    if (!selectedFolderId) return 'team';
    if (rowByCollaborationId.has(selectedFolderId)) return 'collaboration';
    if (rowByCampaignId.has(selectedFolderId)) return 'campaign';
    if (rowByBrandId.has(selectedFolderId)) return 'brand';
    if (rowByTeamId.has(selectedFolderId)) return 'team';
    return 'team';
  }, [rowByBrandId, rowByCampaignId, rowByCollaborationId, rowByTeamId, selectedFolderId]);

  const requestFolderId = useMemo(() => {
    if (isSwitchingTeam) return null;
    return selectedFolderId ?? userTeamId ?? teams?.[0]?.id ?? null;
  }, [isSwitchingTeam, selectedFolderId, teams, userTeamId]);

  const fallbackFolderId = useMemo(() => {
    if (!requestFolderId) return null;
    if (folderType === 'team') return requestFolderId;
    if (userTeamId) return userTeamId;

    if (folderType === 'brand') return rowByBrandId.get(requestFolderId)?.team_id ?? null;
    if (folderType === 'campaign') return rowByCampaignId.get(requestFolderId)?.team_id ?? null;
    if (folderType === 'collaboration')
      return rowByCollaborationId.get(requestFolderId)?.team_id ?? null;

    return null;
  }, [
    folderType,
    requestFolderId,
    rowByBrandId,
    rowByCampaignId,
    rowByCollaborationId,
    userTeamId
  ]);

  const effectiveSearchText = useMemo(() => debouncedSearchText.trim(), [debouncedSearchText]);
  const isSearching = effectiveSearchText.length > 0;

  const baseQuery = useContentHubSearch({
    folderId: requestFolderId,
    fallbackFolderId,
    text: null,
    pageSize: 24,
    nonce: selectionNonce
  });

  const searchQuery = useContentHubSearch({
    folderId: requestFolderId,
    fallbackFolderId,
    text: effectiveSearchText || null,
    pageSize: 24,
    enabled: isSearching,
    nonce: selectionNonce
  });

  const autoCompletion = useContentHubAutoCompletion({
    folderId: requestFolderId,
    text: searchText,
    enabled: true
  });

  const autoCompleteOptions = useMemo(() => {
    return autoCompletion.options.map((v) => ({ value: v }));
  }, [autoCompletion.options]);

  const shouldShowAutoComplete = isSearchFocused && autoCompleteOptions.length > 0;

  const selectedRow = useMemo(() => {
    if (!selectedFolderId) return null;
    if (folderType === 'collaboration') return rowByCollaborationId.get(selectedFolderId) ?? null;
    if (folderType === 'campaign') return rowByCampaignId.get(selectedFolderId) ?? null;
    if (folderType === 'brand') return rowByBrandId.get(selectedFolderId) ?? null;
    if (folderType === 'team') return rowByTeamId.get(selectedFolderId) ?? null;
    return null;
  }, [
    folderType,
    rowByBrandId,
    rowByCampaignId,
    rowByCollaborationId,
    rowByTeamId,
    selectedFolderId
  ]);

  const applyInfluencerFilter = useMemo(() => {
    const selected = normalizeHandle(selectedInfluencerHandle);
    if (!selected) return (items: Api.Client.ContentHubMedia[]) => items;

    return (items: Api.Client.ContentHubMedia[]) =>
      items.filter((m) => normalizeHandle(m.influencer_handle) === selected);
  }, [selectedInfluencerHandle]);

  const filterBySelection = useMemo(() => {
    return (items: Api.Client.ContentHubMedia[]) => {
      if (!selectedFolderId) return [];
      if (folderType === 'team') return items.filter((m) => m.team_id === selectedFolderId);
      if (folderType === 'brand') return items.filter((m) => m.brand_id === selectedFolderId);
      if (folderType === 'campaign') return items.filter((m) => m.campaign_id === selectedFolderId);

      // collaboration
      const campaignId = selectedRow?.campaign_id ?? null;
      const handle = normalizeHandle(selectedRow?.influencer_handle) || null;
      const name = normalizeName(selectedRow?.influencer_name) || null;

      return items.filter((m) => {
        if (m.collaboration_id === selectedFolderId) return true;
        if (!campaignId) return false;

        const mediaHandle = normalizeHandle(m.influencer_handle);
        const mediaName = normalizeName(m.influencer_name);
        const handleMatch = Boolean(handle) && mediaHandle === handle;
        const nameMatch = Boolean(name) && mediaName === name;

        return m.campaign_id === campaignId && (handleMatch || nameMatch);
      });
    };
  }, [folderType, selectedFolderId, selectedRow]);

  const visibleMedias = useMemo(() => {
    if (isSearching) {
      const q = effectiveSearchText.toLowerCase();

      const serverScoped = applyInfluencerFilter(filterBySelection(searchQuery.medias ?? []));
      const baseScoped = applyInfluencerFilter(filterBySelection(baseQuery.medias ?? []));
      const clientMatches = baseScoped.filter((m) => (m.filename ?? '').toLowerCase().includes(q));

      // Merge: keep obvious substring matches even if backend returns non-matching results.
      // This explains (and fixes) cases like query "8371" where backend might return something else.
      const seen = new Set<string>();
      const merged: Api.Client.ContentHubMedia[] = [];
      [...clientMatches, ...serverScoped].forEach((m) => {
        const key = `${m.media_id}:${m.contenthub_url}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(m);
      });

      return merged;
    }

    return applyInfluencerFilter(filterBySelection(baseQuery.medias ?? []));
  }, [
    isSearching,
    effectiveSearchText,
    applyInfluencerFilter,
    filterBySelection,
    searchQuery.medias,
    baseQuery.medias
  ]);

  // If server-side search returns 0 results, progressively load more base pages and apply
  // client-side substring matching over filenames. This makes search behave as users expect
  // (e.g. "714" matches "IMG_6C71461DDE04-1") even if the backend search is prefix/token-based.
  const baseSearchPrefetchRef = useRef<{ key: string; pagesFetched: number }>({
    key: '',
    pagesFetched: 0
  });

  useEffect(() => {
    if (!isSearching) {
      baseSearchPrefetchRef.current = { key: '', pagesFetched: 0 };
      return;
    }

    const q = effectiveSearchText.toLowerCase();
    const serverScoped = applyInfluencerFilter(filterBySelection(searchQuery.medias ?? []));
    const hasAnyServerSubstringMatch = serverScoped.some((m) =>
      (m.filename ?? '').toLowerCase().includes(q)
    );
    if (hasAnyServerSubstringMatch) return;

    const key = `${requestFolderId ?? ''}:${effectiveSearchText}:${selectionNonce}`;
    if (baseSearchPrefetchRef.current.key !== key) {
      baseSearchPrefetchRef.current = { key, pagesFetched: 0 };
    }

    if (!baseQuery.hasMore) return;
    if (baseQuery.isFetchingNextPage || baseQuery.isLoading) return;

    // Safety cap: avoid fetching an unbounded number of pages due to bad search strings.
    if (baseSearchPrefetchRef.current.pagesFetched >= 5) return;

    baseSearchPrefetchRef.current.pagesFetched += 1;
    baseQuery.fetchNextPage();
  }, [
    applyInfluencerFilter,
    baseQuery,
    effectiveSearchText,
    filterBySelection,
    isSearching,
    requestFolderId,
    searchQuery.medias,
    selectionNonce
  ]);

  const {
    hasMore,
    isLoading: isLoadingSearch,
    isFetchingNextPage,
    fetchNextPage
  } = isSearching && (searchQuery.medias ?? []).length > 0 ? searchQuery : baseQuery;

  const visibleLenRef = useRef(0);
  const hasMoreRef = useRef(false);

  useEffect(() => {
    visibleLenRef.current = visibleMedias.length;
  }, [visibleMedias.length]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    // Reset per selection/search context
    setLoadMoreDisabled(false);
  }, [selectedFolderId, effectiveSearchText, isSearching, selectionNonce]);

  const getMediaKey = (m: Api.Client.ContentHubMedia) => `${m.media_id}:${m.contenthub_url}`;

  const loadMore = async () => {
    if (loadMoreDisabled) return;
    if (isLoadingSearch || isFetchingNextPage || isAutoLoadingMore) return;

    setIsAutoLoadingMore(true);
    const delay = () =>
      new Promise<void>((resolve) => {
        setTimeout(resolve, 0);
      });

    try {
      // Fetch up to a few pages until we actually get new *visible* items for the selection.
      // This avoids an "infinite spinner" when backend paging returns items outside our selection.
      const fetchUntilVisible = async (attempt: number, startLen: number): Promise<boolean> => {
        if (attempt >= 5) return false;
        if (!hasMoreRef.current) return false;

        await fetchNextPage();
        await delay();

        if (visibleLenRef.current > startLen) return true;
        return fetchUntilVisible(attempt + 1, startLen);
      };

      const startLen = visibleLenRef.current;
      const didIncrease = await fetchUntilVisible(0, startLen);
      if (didIncrease) return;

      // If we couldn't increase visible items, disable further auto-load for this context.
      setLoadMoreDisabled(true);
    } finally {
      setIsAutoLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!selectionMode) return;
    const visibleKeys = new Set(visibleMedias.map(getMediaKey));
    setSelectedMediaKeys((prev) => {
      const next = new Set<string>();
      prev.forEach((key) => {
        if (visibleKeys.has(key)) next.add(key);
      });
      // next is always a subset of prev; if sizes match, the sets are identical
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [selectionMode, visibleMedias]);

  const selectedCount = selectedMediaKeys.size;

  const onToggleSelected = (mediaKey: string) => {
    setSelectedMediaKeys((prev) => {
      const next = new Set(prev);
      if (next.has(mediaKey)) next.delete(mediaKey);
      else next.add(mediaKey);
      return next;
    });
  };

  const downloadSelected = () => {
    if (!selectionMode || selectedMediaKeys.size === 0 || isDownloading) return;

    const items = visibleMedias.filter((m) => selectedMediaKeys.has(getMediaKey(m)));

    const downloadOne = async (url: string, filename: string) => {
      // Prefer true "save to disk" downloads via Blob + object URL.
      // If CORS blocks fetching, fall back to a direct anchor click.
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Download failed: ${response.status}`);

        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename || 'download';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
      } catch {
        const a = document.createElement('a');
        a.href = url;
        a.download = filename || 'download';
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    };

    setIsDownloading(true);
    items
      .reduce((p, m) => p.then(() => downloadOne(m.contenthub_url, m.filename)), Promise.resolve())
      .finally(() => {
        setIsDownloading(false);
        setSelectionMode(false);
        setSelectedMediaKeys(new Set());
      });
  };

  const heading = useMemo(() => {
    const showTeamSelector = memoTeams.length > 1;

    const handleTeamSwitch = (selectedTeamId: string) => {
      // Reset local UI state so the tree and content rebuild cleanly for the new team
      setSelectedFolderId(null);
      setSearchText('');
      setSelectedInfluencerHandle(null);
      setSelectionMode(false);
      setSelectedMediaKeys(new Set());
      setLoadMoreDisabled(false);
      setIsSearchFocused(false);

      teamSwitch(selectedTeamId);
    };

    return (
      <div className={styles.heading}>
        <Typography sizes={['h2']}>{t('navigationLink.contentHub')}</Typography>

        <div className={styles.controlsRow}>
          {showTeamSelector && (
            <div className={styles.teamSelectorWrap}>
              <TeamSelector
                teams={memoTeams}
                currentTeamId={user?.team?.team_id}
                currentTeamName={user?.team?.name}
                onChange={handleTeamSwitch}
                disabled={isLoadingTeams}
                isLoading={isSwitchingTeam}
              />
            </div>
          )}

          <div className={styles.searchWrap}>
            <AutoComplete
              value={searchText}
              options={autoCompleteOptions}
              open={shouldShowAutoComplete}
              onSearch={(value) => {
                setSearchText(value);
                // Only treat influencer as selected when user explicitly chooses an option
                setSelectedInfluencerHandle(null);

                // If user is typing, ensure suggestions can show even if we previously closed them
                // after selecting an option. Selection can also trigger onSearch; suppress once.
                if (suppressNextAutoOpenRef.current) {
                  suppressNextAutoOpenRef.current = false;
                  return;
                }
                setIsSearchFocused(true);
              }}
              onSelect={(value) => {
                suppressNextAutoOpenRef.current = true;
                setSearchText(value);
                setSelectedInfluencerHandle(value);
                setIsSearchFocused(false);
              }}
              filterOption={false}
              getPopupContainer={(triggerNode) => triggerNode.parentElement ?? document.body}
              dropdownStyle={{ zIndex: 9999 }}
              style={{ width: '100%' }}
            >
              <Input
                className={styles.searchInput}
                value={searchText}
                placeholder={t('contentHub.searchPlaceholder')}
                prefix={<Search size={16} />}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => {
                  // allow click on suggestion before closing
                  window.setTimeout(() => setIsSearchFocused(false), 150);
                }}
                suffix={
                  searchText ? (
                    <button
                      type="button"
                      className={styles.clearButton}
                      onClick={() => {
                        setSearchText('');
                        setSelectedInfluencerHandle(null);
                      }}
                      aria-label={t('contentHub.clearSearch')}
                    >
                      <X size={16} />
                    </button>
                  ) : null
                }
              />
            </AutoComplete>
          </div>

          <div className={styles.headerActions}>
            <Button
              theme="cta"
              onClick={() => {
                if (selectionMode) {
                  // Cancel selection mode / reset selected state
                  setSelectionMode(false);
                  setSelectedMediaKeys(new Set());
                  return;
                }

                setSelectionMode(true);
                setSelectedMediaKeys(new Set());
              }}
              disabled={selectionMode ? false : visibleMedias.length === 0}
            >
              {selectionMode
                ? t('contentHub.selected', { count: selectedCount })
                : t('contentHub.download')}
            </Button>
            <Button
              theme="cta"
              aria-label={t('contentHub.downloadSelected')}
              onClick={downloadSelected}
              disabled={!selectionMode || selectedCount === 0 || isDownloading}
              isLoading={isDownloading}
            >
              <ArrowDown size={16} />
            </Button>
          </div>
        </div>
      </div>
    );
  }, [
    t,
    searchText,
    autoCompleteOptions,
    shouldShowAutoComplete,
    selectedInfluencerHandle,
    selectionMode,
    visibleMedias.length,
    selectedCount,
    isDownloading,
    memoTeams,
    user?.team?.team_id,
    user?.team?.name,
    isLoadingTeams,
    isSwitchingTeam,
    teamSwitch
  ]);

  if (!user.permissions?.show_menu_content_hub) {
    return (
      <div className={styles.emptyState}>
        <div>
          <h3>{t('hub.title')}</h3>
          <p>{t('hub.description')}</p>
        </div>
      </div>
    );
  }

  return (
    <Wrapper heading={heading} hideFooter>
      <div className={isHubEmpty ? styles.layoutNoSidebar : styles.layout}>
        {!isHubEmpty && (
          <aside className={styles.sidebar}>
            {isLoadingPaths ? (
              <div className={styles.sidebarLoading}>
                <Typography sizes={['body']}>{t('contentHub.loadingFolders')}</Typography>
              </div>
            ) : (
              <ContentHubTree
                teams={teams}
                selectedFolderId={selectedFolderId}
                onSelect={(id) => {
                  setSelectedFolderId(id);
                  setSelectionNonce((n) => n + 1);
                }}
              />
            )}
          </aside>
        )}

        <main className={styles.main}>
          {isHubEmpty ? (
            <div className={styles.mainEmpty}>
              <Typography sizes={['body']}>{t('contentHub.noFolders')}</Typography>
            </div>
          ) : !selectedFolderId ? (
            <div className={styles.mainEmpty}>
              <Typography sizes={['body']}>{t('contentHub.selectFolder')}</Typography>
            </div>
          ) : visibleMedias.length === 0 && !isLoadingSearch ? (
            <div className={styles.mainEmpty}>
              <Typography sizes={['body']}>{t('contentHub.empty')}</Typography>
            </div>
          ) : (
            <ContentHubMediaGrid
              medias={visibleMedias}
              hasMore={hasMore && !loadMoreDisabled}
              isInitialLoading={isLoadingSearch}
              isFetchingNextPage={isFetchingNextPage || isAutoLoadingMore}
              loadMore={loadMore}
              selectionMode={selectionMode}
              selectedMediaKeys={selectedMediaKeys}
              onToggleSelected={onToggleSelected}
            />
          )}
        </main>
      </div>
    </Wrapper>
  );
}

export function ContentHub() {
  if (isProduction()) return <ContentHubProduction />;
  return <ContentHubStaging />;
}
